import type { ButtonHTMLAttributes, HTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';

function classes(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(' ');
}

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'quiet' | 'danger';
  compact?: boolean;
};

export function Button({ variant = 'primary', compact = false, className, ...props }: ButtonProps) {
  return <button className={classes('button', `button--${variant}`, compact && 'button--compact', className)} {...props} />;
}

export type FieldProps = InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  hint?: string;
};

export function Field({ label, hint, id, className, ...props }: FieldProps) {
  const fieldId = id ?? `field-${props.name ?? label.toLowerCase().replaceAll(' ', '-')}`;
  return (
    <label className={classes('field', className)} htmlFor={fieldId}>
      <span className="field__label">{label}</span>
      <input id={fieldId} className="input" {...props} />
      {hint ? <span className="field__hint">{hint}</span> : null}
    </label>
  );
}

export function PageHeader({ eyebrow, heading, description, actions }: { eyebrow: string; heading: string; description?: string; actions?: ReactNode }) {
  return (
    <header className="page-header">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h1>{heading}</h1>
        {description ? <p className="page-header__description">{description}</p> : null}
      </div>
      {actions ? <div className="page-header__actions">{actions}</div> : null}
    </header>
  );
}

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={classes('card', className)} {...props} />;
}

export function Badge({ tone = 'neutral', children }: { tone?: 'neutral' | 'signal' | 'alert'; children: ReactNode }) {
  return <span className={`badge badge--${tone}`}>{children}</span>;
}

export function Loading({ label = 'Loading intelligence' }: { label?: string }) {
  return <div className="loading" role="status"><span className="loading__line" />{label}</div>;
}

export function Empty({ heading, body }: { heading: string; body: string }) {
  return <div className="empty"><span className="empty__mark">Ø</span><h2>{heading}</h2><p>{body}</p></div>;
}

export function ErrorNotice({ message }: { message: string }) {
  return <div className="notice notice--error" role="alert">{message}</div>;
}

